import { useMenuStore } from "../zustand/store/useMenuStore";
import * as Icons from '../images/icons/icons_component';
import * as Gnb from '../images/icons/icons_gnb';
import * as Theme from '../images/icons/icons_theme';

/**
 * @SubIconHook 메뉴에 따라 달라지는 배경색에 맞춘 동적 아이콘 훅
 <sample>
 -- useSubIcon import 후 필요한 아이콘만 꺼내와서 사용하기
    import { useSubIcon } from "../hooks/useSubIcon";
    const { graySubIcon, primarySubIcon, whiteSubIcon, hoverSubIcon } = useSubIcon();
 */

/**
 * ─────────────────────────────────────────────────────────────
 * [메뉴 추가 시 아이콘 매칭 방법]
 * 아이콘은 메뉴의 MENUFLAG(EMSINFO_MENU.MENUFLAG) 기준으로 매칭 (메뉴 순서가 바뀌거나 중간에 추가돼도 매칭이 밀리지 않기 위함)
 * 1. 기존 모양 재사용: 아래 GNB_GLYPHS에 「MENUFLAG: Gnb.GnbXX,」 한 줄만 추가
 *    (모양 목록과 설명은 icons_gnb.js 참고. 색상은 카테고리에 맞춰 자동 적용됨)
 * 2. 새 모양이 필요할 때: 24x24 "단일 색상" svg 1개를 받아 fill 값을 "currentColor"로 바꿔 icon_gnb/에 추가 → icons_gnb.js에 import/export
 *    한 줄 추가 → GNB_GLYPHS에 매칭. (색상별로 svg를 여러 개 만들 필요 없음)
 * 3. 매칭을 깜빡해도 미사용 모양 중 하나가 기본 아이콘으로 자동 배정되므로 화면이 깨지지 않음 (renderGnbIcons 참고)
 * ─────────────────────────────────────────────────────────────
 */
const GNB_GLYPHS = {
    // ── 표준(3)/외상(21) 공통 메뉴 ──
    8:    Gnb.Gnb01,  // 표준정보조회
    46:   Gnb.Gnb02,  // 데이터 검토
    39:   Gnb.Gnb05,  // 입력현황 모니터링
    // ── 표준(3) ──
    767:  Gnb.Gnb03,  // 예외전송관리
    1147: Gnb.Gnb04,  // 충실도 정보관리
    1350: Gnb.Gnb06,  // 정보전송 점검표(NEDIS)
    54:   Gnb.Gnb07,  // 진료내역분석
    108:  Gnb.Gnb08,  // 전송 및 이용현황
    // ── 외상(21) ──
    1480: Gnb.Gnb03,  // 예외 전송 관리
    1270: Gnb.Gnb04,  // 외상 충실도 정보관리
    1458: Gnb.Gnb12,  // 외상환자 내원현황
    1459: Gnb.Gnb13,  // 외상환자 내원유형
    1460: Gnb.Gnb14,  // 외상환자 진료정보
    1333: Gnb.Gnb15,  // 인력 관리
    1368:  Gnb.Gnb16, // 실적 관리
    1449:  Gnb.Gnb17, // 수련기관 실적관리
    // ── 관리(9) ──
    3001: Gnb.Gnb18,  // 게시판
    4001: Gnb.Gnb05,  // Agent 관리
    5000: Gnb.Gnb03,  // NEDIS 시스템관리
    2000: Gnb.Gnb19,  // 관리자
};

// GNB_GLYPHS에 매칭이 없는 메뉴에 쓰이는 기본 아이콘 풀 (icons_gnb.js의 전체 글리프)
const GLYPH_POOL = Object.values(Gnb);

// menuTree 순서대로 지정 색상 클래스의 대메뉴 아이콘 배열 생성
// 매칭이 없는 메뉴는 "현재 카테고리에서 사용 중이지 않은 모양" 중 MENUFLAG 기준으로 하나를 골라 표출
// (메뉴마다 항상 같은 모양이 유지되고, 같은 화면 안에서 모양이 중복되지 않음. 모양을 고정하고 싶으면 GNB_GLYPHS에 매칭 추가)
const renderGnbIcons = (menuTree, colorClass) => {
    const usedGlyphs = new Set(menuTree.map((menu) => GNB_GLYPHS[menu.MENUFLAG]).filter(Boolean));
    const pool = GLYPH_POOL.filter((glyph) => !usedGlyphs.has(glyph));
    const defaultGlyph = (menuflag) => pool.length > 0 ? pool[Number(menuflag) % pool.length] : Gnb.Gnb01;
    return menuTree.map((menu) => {
        const Glyph = GNB_GLYPHS[menu.MENUFLAG] || defaultGlyph(menu.MENUFLAG);
        return <Glyph key={menu.MENUFLAG} className={colorClass} />;
    });
};

// 대메뉴 탭 글리프: 탭 모양은 각 탭 고유(변하지 않음), 색상만 클릭된 대메뉴를 따라감
const TAB_GLYPHS = { 3: Theme.TabSt, 21: Theme.TabTr, 9: Theme.TabMng };

// 클릭된 대메뉴 테마(st/tr/ho) 기준 탭 아이콘 묶음 생성
// - 클릭된 탭: primary 고정 / 나머지 탭: 클릭된 대메뉴의 연한색(soft), hover 시 primary
const buildTabIcons = (activeCategory, theme) => {
    const tabs = {};
    [3, 21, 9].forEach((tabCategory) => {
        const TabGlyph = TAB_GLYPHS[tabCategory];
        tabs[tabCategory] = tabCategory === activeCategory
            ? { normal: <TabGlyph className={`${theme}_primary`} />, hover: <TabGlyph className={`${theme}_primary`} /> }
            : { normal: <TabGlyph className={`${theme}_soft`} />, hover: <TabGlyph className={`${theme}_primary`} /> };
    });
    return tabs;
};

export const useSubIcon = () => {

    const clickedCategory = useMenuStore((state) => state.clickedCategory);
    const menuData = useMenuStore((state) => state.menuData);
    const menuTree = menuData[clickedCategory]?.menuTree || [];

    // clickedCategory === 9(관리) / 3(표준) / 21(외상)
    const stMenu = clickedCategory === 3;
    const trMenu = clickedCategory === 21;

    // 클릭된 카테고리의 색상 클래스 접두어 (관리·홈은 ho 색상 공용, 색상 클래스는 common.css 참고)
    const theme = stMenu ? 'st' : trMenu ? 'tr' : 'ho';

    const graySubIcon = renderGnbIcons(menuTree, 'co_light');
    const primarySubIcon = renderGnbIcons(menuTree, `${theme}_primary`);
    const whiteSubIcon = renderGnbIcons(menuTree, 'co_white');
    const hoverSubIcon = renderGnbIcons(menuTree, `${theme}_bright`);

    // top menu list: topIcons[클릭된 대메뉴 테마][탭 카테고리]로 조회 (TopMenu.jsx)
    // 스위치 UX: 클릭된 탭만 primary, 나머지 탭은 클릭된 대메뉴의 연한색(soft) 유지
    const topIcons = {
        // 홈일 때 아이콘 묶음 (관리 primary 색상 고정)
        home: {
            3: <Theme.TabSt className='ho_primary' />,
            21: <Theme.TabTr className='ho_primary' />,
            9: <Theme.TabMng className='ho_primary' />
        },
        st: buildTabIcons(3, 'st'),   // 표준 클릭 시
        tr: buildTabIcons(21, 'tr'),  // 외상 클릭 시
        mng: buildTabIcons(9, 'ho')   // 관리 클릭 시
    };

    // fav icon
    const favIcon = <Theme.Star className={`${theme}_primary`} />;
    const hoverFavIcon = <Theme.Star className={`${theme}_bright`} />;
    const favFullIcon = <Theme.StarFull className={`${theme}_primary`} />;
    const hoverFavFullIcon = <Theme.StarFull className={`${theme}_bright`} />;
    const favHereIcon = stMenu || trMenu
        ? <Theme.Here16 className={`${theme}_primary`} />
        : <Theme.HereBig16 className='ho_dark' />;

    // pagination icon
    const hoverFirstPageIcon = <Theme.PageFirst className={`${theme}_bright`} />;
    const hoverLastPageIcon = <Theme.PageLast className={`${theme}_bright`} />;
    const hoverPrevIcon = <Theme.PagePrev className={`${theme}_bright`} />;
    const hoverNextIcon = <Theme.PageNext className={`${theme}_bright`} />;

    // data grid icon
    const SearchIcon = <Theme.Search16 className={`${theme}_bright`} />;
    const NullIcon = trMenu ? <Icons.NullSalgu/> : <Icons.NullDalgu/>;
    const EmptyIcon = trMenu ? <Icons.EmptySalgu/> : <Icons.EmptyDalgu/>;
    const NotiIcon = trMenu ? <Icons.NotiSalgu/> : <Icons.NotiDalgu/>;

    return {
        graySubIcon,
        primarySubIcon,
        whiteSubIcon,
        hoverSubIcon,
        topIcons,
        favIcon,
        hoverFavIcon,
        favFullIcon,
        hoverFavFullIcon,
        favHereIcon,
        hoverFirstPageIcon,
        hoverLastPageIcon,
        hoverPrevIcon,
        hoverNextIcon,
        SearchIcon,
        NullIcon,
        EmptyIcon,
        NotiIcon
    };
};
